import { Component, OnInit, Input, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ListenService, BodensteckbriefEntry } from './listen.service';
import { buildNamensmatrix, Namensmatrix, NamensmatrixRow, NamensmatrixCell } from './namensmatrix';

/** Sortier-Schluessel: 'kuerzel' | 'anzahl' | 'col:<suffix>' (eine Substrat-Spalte). */
type SortKey = string;

/** Wiederherstellbarer Filter-/Sortierzustand der Matrix (serialisierbar, wird
 *  beim Zellklick in den History-State gelegt, damit „Zurück" im Viewer die
 *  Matrix mit gleicher Sicht wieder öffnet). */
export interface MatrixViewState {
  searchTerm: string;
  filterSuffix: string;
  hideEmptyCols: boolean;
  sortKey: SortKey;
  sortDir: 'asc' | 'desc';
}

/** History-State, den ein Zellklick auf der HBF-Seite hinterlässt. */
export interface NamensmatrixNavState {
  fromMatrix: boolean;
  matrixView: MatrixViewState;
  /** URL des ursprünglichen Aufrufers (Viewer-Liste / Editor). */
  matrixOrigin: string;
}

/**
 * Namensmatrix-Modal: klickbare/sortierbare/filterbare Kreuztabelle der
 * Feinbodenform-Codes (Lokalnamen-Kuerzel x Substrat-Suffix).
 *
 * Aufrufbar aus dem Bodenformenkatalog-Viewer und dem Steckbrief-Editor (über
 * den {@link NamensmatrixService}). Ein Klick auf eine Treffer-Zelle schliesst
 * das Modal und navigiert auf die zugehoerige HBF-Seite im Viewer
 * (`/bodenformenkatalog/:id`). Dabei wird im History-State vermerkt, dass der
 * Sprung aus der Matrix kam – der „Zurück"-Knopf im Viewer öffnet die Matrix
 * dann mit gleichem Filter-/Sortierzustand wieder (statt blind zurückzublättern).
 * Schließt man die Matrix ohne Zellklick, geht es zurück zum Ursprung.
 *
 * Datenquelle ist dieselbe zentrale Liste wie bei Viewer/Editor
 * (`Bodensteckbriefe`); die Matrix wird beim Oeffnen einmal aufgebaut.
 */
@Component({
  standalone: false,
  selector: 'bp-namensmatrix-modal',
  templateUrl: './namensmatrix-modal.component.html',
  styleUrls: ['./namensmatrix-modal.component.css'],
})
export class NamensmatrixModalComponent implements OnInit {
  private listenService = inject(ListenService);
  private router = inject(Router);
  public activeModal = inject(NgbActiveModal);

  /** URL des Aufrufers – Ziel beim Schließen ohne Zellklick (vom Service gesetzt). */
  @Input() originUrl = '';
  /** Filter-/Sortierzustand zum Wiederherstellen (beim Wiederöffnen via „Zurück"). */
  @Input() initialState?: MatrixViewState;

  loading = true;
  matrix: Namensmatrix = { cols: [], rows: [], duplicates: [] };

  /** Freitext-Filter: matcht Kuerzel, jeden vollen Lokalnamen der Zeile sowie
   *  belegte Substrat-Suffixe. */
  searchTerm = '';
  /** Direkt-Filter auf EIN Substrat-Suffix ('' = alle Spalten). */
  filterSuffix = '';
  /** Spalten ohne Treffer (im aktuellen Zeilen-Filter) ausblenden. */
  hideEmptyCols = true;

  sortKey: SortKey = 'kuerzel';
  sortDir: 'asc' | 'desc' = 'asc';

  /** Anzeige-Sicht (gefiltert + sortiert). */
  displayRows: NamensmatrixRow[] = [];
  displayCols: string[] = [];

  async ngOnInit(): Promise<void> {
    if (this.initialState) this.applyState(this.initialState);
    const list = (await this.listenService.getList('Bodensteckbriefe')) as BodensteckbriefEntry[] | null;
    this.matrix = buildNamensmatrix(list || []);
    this.recompute();
    this.loading = false;
  }

  /** Aktuellen Filter-/Sortierzustand für den History-State einsammeln. */
  captureState(): MatrixViewState {
    return {
      searchTerm: this.searchTerm,
      filterSuffix: this.filterSuffix,
      hideEmptyCols: this.hideEmptyCols,
      sortKey: this.sortKey,
      sortDir: this.sortDir,
    };
  }

  private applyState(s: MatrixViewState): void {
    this.searchTerm = s.searchTerm;
    this.filterSuffix = s.filterSuffix;
    this.hideEmptyCols = s.hideEmptyCols;
    this.sortKey = s.sortKey;
    this.sortDir = s.sortDir;
  }

  // ========== Filter / Sortierung ==========

  recompute(): void {
    const term = (this.searchTerm || '').toLowerCase().trim();
    let rows = this.matrix.rows;
    if (this.filterSuffix) rows = rows.filter(r => r.cells.has(this.filterSuffix));
    if (term) rows = rows.filter(r => this.matchRow(r, term));
    rows = [...rows].sort((a, b) => this.compareRows(a, b));

    let cols = this.filterSuffix ? [this.filterSuffix] : this.matrix.cols;
    if (this.hideEmptyCols) cols = cols.filter(c => rows.some(r => r.cells.has(c)));

    this.displayRows = rows;
    this.displayCols = cols;
  }

  private matchRow(r: NamensmatrixRow, term: string): boolean {
    if (r.kuerzel.toLowerCase().includes(term)) return true;
    if (r.namen.some(n => n.toLowerCase().includes(term))) return true;
    for (const suffix of r.cells.keys()) if (suffix.toLowerCase().includes(term)) return true;
    return false;
  }

  /** Spaltenkopf-Klick: gleiche Spalte -> Richtung umdrehen, sonst neu aufsteigend.
   *  Bei Substrat-Spalten aufsteigend = Treffer zuerst. */
  setSort(key: SortKey): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.recompute();
  }

  sortIcon(key: SortKey): string {
    if (this.sortKey !== key) return 'bi-arrow-down-up';
    return this.sortDir === 'asc' ? 'bi-caret-down-fill' : 'bi-caret-up-fill';
  }

  private compareRows(a: NamensmatrixRow, b: NamensmatrixRow): number {
    const dir = this.sortDir === 'desc' ? -1 : 1;
    let cmp: number;
    if (this.sortKey === 'anzahl') {
      cmp = (a.anzahl - b.anzahl) * dir;
    } else if (this.sortKey.startsWith('col:')) {
      const suffix = this.sortKey.slice(4);
      // Treffer (hat Zelle) = 0, kein Treffer = 1 -> bei asc kommen Treffer zuerst.
      cmp = ((a.cells.has(suffix) ? 0 : 1) - (b.cells.has(suffix) ? 0 : 1)) * dir;
    } else {
      cmp = a.kuerzel.localeCompare(b.kuerzel, 'de') * dir;
    }
    // Stabiler Tiebreak: immer nach Kuerzel.
    if (cmp === 0) cmp = a.kuerzel.localeCompare(b.kuerzel, 'de');
    return cmp;
  }

  // ========== Aktionen ==========

  /** Treffer-Zelle angeklickt: Modal schliessen und auf die HBF-Seite (den
   *  Steckbrief) im Viewer navigieren. Im History-State merken wir uns, dass der
   *  Sprung aus der Matrix kam (+ Filterzustand + Ursprung), damit „Zurück" im
   *  Viewer die Matrix mit gleicher Sicht wieder öffnet. */
  onCell(cell: NamensmatrixCell): void {
    const state: NamensmatrixNavState = {
      fromMatrix: true,
      matrixView: this.captureState(),
      matrixOrigin: this.originUrl,
    };
    this.activeModal.close();
    // replaceUrl: Der Zellsprung ERSETZT den Viewer-/Editor-Eintrag statt ihn zu
    // stapeln. So bleibt der Viewer ein einziger History-Eintrag über dem App-
    // Ursprung (gleiche Logik wie Prev/Next). „Zurück" öffnet dann via Marker die
    // Matrix; schließt man sie, geht es sauber zum Aufrufer zurück - ohne dass
    // sich bei mehreren Matrix-Zyklen Katalog-Einträge im Verlauf ansammeln.
    this.router.navigate(['/bodenformenkatalog', cell.steckbriefId], { state, replaceUrl: true });
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filterSuffix = '';
    this.recompute();
  }
}
